import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { CultureService } from 'src/app/shared/culture/culture.service';
import { Link } from '../../shared/interfaces/link';
import { MODULE, SiteModulesService } from '../../shared/services/site-modules.service';
import { CompanyService } from '../../shared/services/company.service';
import { Company } from '../../shared/interfaces/company';
import { BRAND } from 'src/app/shared/brand';
import { FooterLink } from './components/links/links.component';

const SHOPPING_LINKS: FooterLink[] = [
        { key: 'trust-and-safety', label: 'Trust & Safety', url: '/trust' },
        { key: 'about-us', label: 'About Us', url: '/about' },
        { key: 'contact-us', label: 'Contact Us', url: '/contact' },
        { key: 'shops', label: 'Shops', url: '/shops' },
        { key: 'blog', label: 'Blog', url: '/blog' },
        { label: 'PrimeCommerce', url: '/primecommerce' },
];

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss']
})
export class FooterComponent {
    /// The brand, for the template (shared/brand.ts).
    readonly brand = BRAND;

    date = new Date();
    year = this.date.getFullYear();

    readonly company$: Observable<Company | null>;

    /// "Shopping Made Easy": the Shops link only while the shops directory is switched on (the owner's Modules page).
    readonly shoppingLinks$: Observable<Link[]>;

    /// The "Sell on PrimeHat" box: only on pages for sellers, never in the shop (the owner, 2026-10-22).
    readonly sellerPage$: Observable<boolean>;

    constructor(companies: CompanyService, modules: SiteModulesService, private router: Router, private culture: CultureService) {
        this.company$ = companies.company$;
        this.shoppingLinks$ = modules.on$(MODULE.directory).pipe(
            startWith(false),
            distinctUntilChanged(),
            map(shops => SHOPPING_LINKS.filter(link => shops || link.url !== '/shops')));
        this.sellerPage$ = router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            startWith(null),
            map(() => this.isSellerPage()),
            distinctUntilChanged());
    }

    /// A page for sellers, in any language: the sell pages (/sell, its handbook and stories), PrimeCommerce, the seller
    /// tools, the blog's sellers' and stories' tabs, and a post written for sellers (its category, from the resolver).
    private isSellerPage(): boolean {
        const tree = this.router.parseUrl(this.culture.bare(this.router.url));
        const path = '/' + (tree.root.children.primary?.segments.map(s => s.path).join('/') || '');
        if (SELLER_SECTIONS.some(s => path === s || path.startsWith(s + '/'))) { return true; }
        if (path === '/blog' && SELLER_POSTS.includes(tree.queryParams.cat)) { return true; }

        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) { route = route.firstChild; }
        return SELLER_POSTS.includes(route.data?.blog?.category);
    }
}

/// The site's sections for sellers.
const SELLER_SECTIONS = ['/sell', '/primecommerce', '/tools'];

/// The kinds of post written for sellers (Blog.Category; the blog's tabs).
const SELLER_POSTS = ['sellers', 'stories'];
